import React, { memo, useState, useEffect, useContext } from 'react'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { IFullStop } from 'types'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { UserContext } from 'apps/fleet-planner/context'
import { IStop } from 'apps/fleet-planner/types/loads'
import {
  getAreTimesVisible,
  getSequenceOrder,
} from 'apps/fleet-planner/utils/checkinCheckoutTimesHelpers'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { formatCityState } from 'utils/formatCityState'
import CheckinCheckoutTimesStopDetails from './CheckInCheckOutTimesStopDetails'
import { InOutTimes } from './stops/InOutTimes'

interface IStopInlineEditProps {
  marketplaceStop?: IFullStop
  stop: IStop
  stops: IStop[]
  sequence: number
  handleStopChange: (stops: IStop[]) => void
  newStatus: LOAD_STATE
  newStopSequence: string
}

export const CheckinCheckoutTimesStopContainer = memo<IStopInlineEditProps>(
  ({ handleStopChange, marketplaceStop, newStatus, newStopSequence, sequence, stop, stops }) => {
    const { timeFormats } = useContext(UserContext)
    const { address, stopSequence, stopType } = stop || {}
    const { locality, subdivisionCode } = address || {}
    const [change, setChange] = useState<Partial<IStop>>(stop)
    useEffect(() => setChange(stop), [stop])

    const updateStopChangeHandler = (s: Partial<IStop>) => {
      const stopId = s.id
      const updatedStops = stops.map(currentStop => {
        if (currentStop.id === stopId) {
          return { ...currentStop, ...s }
        }
        return currentStop
      })
      handleStopChange(updatedStops)
    }

    const updateStopAttributes = (attributes: Partial<IStop>) => {
      const newStop = { ...change, ...attributes } as IStop
      updateStopChangeHandler(newStop)
    }

    const sequenceOrderNumber = getSequenceOrder(stops, newStatus, newStopSequence)
    const { isCheckinVisible, isCheckoutVisible, isStopVisible } = getAreTimesVisible(
      newStatus,
      sequenceOrderNumber,
      stopSequence
    )

    const isStopLate = (): boolean => {
      const { checkedInAt, endAt, startAt } = stop || {}
      const lateAt = endAt || startAt
      if (checkedInAt && lateAt) {
        return new Date(checkedInAt) > new Date(lateAt)
      }
      return false
    }

    const isLateArrival = isStopLate()

    return (
      <>
        {isStopVisible && (
          <>
            <div className='flex items-center mt4 mb3'>
              <MapMarker type={stopType} sequence={sequence} />
              <span className='body-medium pl2 pr4'>
                {capitalize(stopType)} {sequence > 0 ? sequence : ''}:
                {` ${formatCityState(locality, subdivisionCode)}`}
              </span>
            </div>
            <CheckinCheckoutTimesStopDetails stop={stop} />
            <InOutTimes
              marketplaceStop={marketplaceStop}
              stop={stop}
              isCheckinVisible={isCheckinVisible}
              isCheckoutVisible={isCheckoutVisible}
              onStopChange={updateStopAttributes}
              timeFormat={timeFormats.stopTimeInput}
            />
            {isLateArrival && (
              <div className='mt4 mb6'>
                <InlineMessage
                  message='This will notify the shipper that the check-in is late'
                  type={INLINE_MESSAGE_VARIANTS.INFO}
                />
              </div>
            )}
          </>
        )}
      </>
    )
  }
)
